import {
  websiteElementCapabilitiesForSlot,
  type SupabaseClient,
  type WebsiteFrameDevice,
  type WebsiteInterfaceLocale,
  type WebsiteTemplateManifest,
} from '@dukanti/shared';
import type { RegionEditor } from '../hooks/use-region-editor';
import { useWebsiteT } from '../hooks/use-website-t';
import { MediaUploadField } from './fields/media-upload-field';
import { SlotDestinationField } from './fields/slot-destination-field';
import { MediaFramingEditor } from './media/media-framing-editor';
import { websiteMediaSlotDevice } from '../lib/media-slot-device';

export interface RegionMediaFormProps {
  readonly editor: RegionEditor;
  readonly storeId: string;
  readonly interfaceLocale: WebsiteInterfaceLocale;
  readonly capturePoster?: (file: File) => Promise<File>;
  /** Epic #228 P5: the element capabilities (removable, clickable) come from the manifest. */
  readonly manifest: WebsiteTemplateManifest;
  readonly client: SupabaseClient;
  readonly templateId: string;
  readonly supabaseUrl: string;
  /** Epic #228 P6: the canvas device; the crop opens on its tab. */
  readonly device?: WebsiteFrameDevice;
  /** Epic #228 P5 (UI/UX P2-2): the merchant tried to close with an unfinished field. */
  readonly finishRequested?: boolean;
}

/**
 * A media region: its image or video in its true frame with the crop editor
 * (epic #228 P6), and, only when the template declares the
 * media clickable (`clickable.linkSlotId`, PD8), its destination. Decorative
 * media has no destination; a banner's destination is its CTA, edited with
 * its text (PD8 option A).
 */
export function RegionMediaForm({
  editor,
  storeId,
  interfaceLocale,
  capturePoster,
  manifest,
  client,
  templateId,
  supabaseUrl,
  device = 'desktop',
  finishRequested = false,
}: RegionMediaFormProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  // The PD8 seam can put the clickable link slot in this region: pick the media by type.
  const slot = editor.slots.find((candidate) => candidate.type === 'media');
  const state = slot ? editor.values[slot.id] : undefined;
  if (!slot || !state) return null;

  const capabilities = websiteElementCapabilitiesForSlot(manifest, slot.id);
  const removable =
    capabilities?.media.find((media) => media.slotId === slot.id)?.removable ?? !slot.required;
  const destination = capabilities?.destination?.anchorsMedia ? capabilities.destination : null;
  const linkSlot = destination
    ? editor.slots.find((candidate) => candidate.id === destination.linkSlotId)
    : undefined;
  const linkState = linkSlot ? editor.values[linkSlot.id] : undefined;

  return (
    <div className="space-y-6">
      <MediaUploadField
        slot={slot}
        state={state}
        interfaceLocale={interfaceLocale}
        storeId={storeId}
        issueKey={editor.fieldIssues[slot.id]}
        capturePoster={capturePoster}
        removable={removable}
        preview={
          <MediaFramingEditor
            slot={slot}
            state={state}
            layoutFamily={manifest.layoutFamily}
            interfaceLocale={interfaceLocale}
            device={device}
            supabaseUrl={supabaseUrl}
            disabled={editor.isUploading}
            // UI/UX P2-3: a hero's mobile or desktop image is framed for its own device only.
            slotDevice={websiteMediaSlotDevice(manifest, slot.id)}
            onApply={(next) => editor.setField(slot.id, next)}
            onLocalWorkChange={(work) => editor.setLocalWork(slot.id, work)}
          />
        }
        onChange={(next) => editor.setField(slot.id, next)}
        onUploadingChange={(uploading) => editor.setUploading(slot.id, uploading)}
      />
      {linkSlot && linkState ? (
        <div
          className="border-border space-y-3 border-t pt-5"
          data-testid="media-destination-section"
          aria-label={t('admin.destination.title')}
          role="group"
        >
          <SlotDestinationField
            slot={linkSlot}
            state={linkState}
            interfaceLocale={interfaceLocale}
            issueKey={editor.fieldIssues[linkSlot.id]}
            onChange={(next) => editor.setField(linkSlot.id, next)}
            client={client}
            storeId={storeId}
            templateId={templateId}
            supabaseUrl={supabaseUrl}
            finishRequested={finishRequested}
          />
        </div>
      ) : null}
    </div>
  );
}
